import type { MachineId, RunRecord, Snapshot } from '../shared/contract';
import { CONTRACT_VERSION } from '../shared/contract';
import { RunDirector } from '../core/run';
import { mount, type Kit } from '../render-kit/src/index';
import { InputMap } from './input';
import { GameLoop } from './loop';
import { Ui } from './ui';
import { Audio } from './audio';
import {
  dailyKey, dailySeed, downloadReceipt, gepaeckBag, loadProgress, newlyUnlocked,
  recordRun, saveProgress, unlockedIds, Vitrine, type Progress, type RunSummary,
} from './meta';
import { LEHRNACHT_COINS, LEHRNACHT_SEED, lehrnachtBeutel } from '../core/lehrnacht';
import { Lehrnacht } from './lehrnacht';
import { ErstmalsWatcher } from './erstmals';
import { Hilfekarte, SlipPrinter } from './slip';
import './style.css';

/**
 * SPIELHÖLLE — the app.
 *
 * Three layers meet here and nowhere else:
 *
 *   core/        the deterministic sim. Fixed 120 Hz. Knows no DOM, no Three, no clock.
 *   render-kit/  the diorama. Interpolates, owns its own clock, knows no rules.
 *   app/         this: input -> log -> sim -> snapshot -> kit + DOM.
 *
 * The arrow only points one way. The renderer never tells the sim anything, the sim never
 * asks the renderer for anything, and every player action becomes a logged `SimInput`
 * before it becomes a change in the world. That is what keeps a run equal to
 * (seed + input log), which is in turn what makes the daily seed, the shareable receipt
 * and the FOTOAUTOMAT possible at all.
 */

const PARAMS = new URLSearchParams(location.search);

/**
 * A seed you can put in a URL, so a run is a link.
 *
 * `?tageslos` plays today's local daily seed instead. Determinism makes that nearly free —
 * but note the honesty constraint in `app/meta.ts`: cross-browser bit-determinism is still
 * unmeasured, so this is a **per-build, local** daily, not a leaderboard, and the UI says
 * so in as many words.
 */
function bootSeed(): number {
  if (PARAMS.has('tageslos')) return dailySeed();
  const q = PARAMS.get('seed');
  if (q && /^\d+$/.test(q)) return Number(q) >>> 0;
  // no seed given: pick one and PUT IT IN THE URL, so any run can be reproduced later.
  const s = (Date.now() ^ (Math.random() * 0xffffffff)) >>> 0;
  const url = new URL(location.href);
  url.searchParams.set('seed', String(s));
  history.replaceState(null, '', url);
  return s;
}

/**
 * IS TONIGHT DIE LEHRNACHT?
 *
 * A new profile's first run *is* the tutorial — no menu, no prompt, no "would you like
 * to play the tutorial?" dialog, because the answer to that question is always no and
 * the game is worse for asking it.
 *
 * The rule has one deliberate subtlety worth stating, because it is what keeps every
 * existing gate in this repository green: **an explicit seed suppresses the Lehrnacht.**
 * A `?seed=` in the address bar means "play *this* run", and the Lehrnacht is not that
 * run — it has a seed of its own. Every E2E, soak, audit, probe and cross-browser harness
 * in `e2e/` navigates with an explicit seed, so none of them was ever going to be sent to
 * school, and none of them needed changing. `?lehrnacht=1` forces it (that is how the new
 * E2E and the framed receipt in Die Vitrine replay it) and `?lehrnacht=0` refuses it.
 */
function wantsLehrnacht(progress: Progress): boolean {
  const forced = PARAMS.get('lehrnacht');
  if (forced === '1') return true;
  if (forced === '0') return false;
  if (PARAMS.has('seed') || PARAMS.has('tageslos')) return false;
  return progress.lehrgeld === null && progress.runs === 0;
}

export interface Game {
  dir: RunDirector;
  kit: Kit;
  ui: Ui;
  loop: GameLoop;
  input: InputMap;
  /** the whole run, as a thing you could paste into a bug report */
  record(): RunRecord;
  destroy(): void;
}

export async function boot(host: HTMLElement, seedOverride?: number): Promise<Game> {
  let progress = loadProgress();
  /**
   * Die Lehrnacht is a real run with a fixed seed and a single floor. Everything that
   * makes it a lesson rather than a run lives in `app/lehrnacht.ts` (the gate and the
   * script) and `core/lehrnacht.ts` (the world shape per beat).
   */
  const lehrnacht = seedOverride === undefined && wantsLehrnacht(progress);
  const seed = lehrnacht ? LEHRNACHT_SEED : (seedOverride ?? bootSeed());
  const daily = !lehrnacht && PARAMS.has('tageslos');

  const stage = document.createElement('div');
  stage.className = 'sh-stage';
  host.appendChild(stage);
  const uiRoot = document.createElement('div');
  uiRoot.className = 'sh-uiroot';
  host.appendChild(uiRoot);

  /**
   * META. Unlocks are *pools only* — new tokens, charms and Gepäck enter the draw and
   * nothing ever gets a bigger number (core-loop.md: "No stat power between runs — runs
   * stay equal for fairness and future daily-seed integrity"). So this feeds the run's
   * item pool and its starting bag, and touches no multiplier anywhere.
   */
  const gepaeck = PARAMS.get('gepaeck') ?? 'hausbeutel';

  const dir = lehrnacht
    ? await RunDirector.create({
      seed,
      // He hands you the marker itself: fifty out, fifty back at sunrise, so the
      // arithmetic of the whole night is one number you can hold in your head.
      startingCoins: LEHRNACHT_COINS,
      // one floor — the Lehrnacht ends at its own small Abrechnung
      floors: 1,
      // ten plain coins, not one enchantment among them, so the first tag the player
      // ever sees is the one they won
      beutel: lehrnachtBeutel(),
      lehrnacht: true,
    })
    : await RunDirector.create({
      seed,
      startingCoins: 260,
      pool: unlockedIds(progress),
      beutel: gepaeckBag(gepaeck),
    });
  const kit = await mount(stage);

  /**
   * AUDIO. A pure sink: it reads the same `SimEvent` feed the renderer does and never
   * talks back, so it cannot affect a replay. It is also entirely synthesized — there is
   * not one audio file in this repository (see render-kit/LICENSES.md).
   */
  const audio = new Audio();
  const unlockAudio = (): void => { void audio.unlock(); };
  for (const ev of ['pointerdown', 'keydown'] as const) {
    window.addEventListener(ev, unlockAudio, { once: false, passive: true });
  }

  /**
   * THE PRINTED-MATTER LAYER. Der Croupier's voice and the reference card.
   *
   * Both are paper, both are Share Tech Mono on `papier`, and neither is a tooltip —
   * ART-BIBLE §5/§10. The slip printer is shared: Die Lehrnacht speaks through it for
   * eight minutes and the one-time first-encounter asides speak through it forever after,
   * so the game only ever has one voice and one place it comes from.
   */
  const slips = new SlipPrinter({ root: uiRoot });
  const hilfe = new Hilfekarte(uiRoot);

  const input = new InputMap({
    target: stage,
    dir,
    onInspect: (on) => ui.setInspecting(on),
    onHelp: (on) => hilfe.show(on),
    onVerb: () => {},
    // a verb key pressed with your hands on nothing. During the Lehrnacht he answers.
    onIgnored: (v) => lehr?.ignored(v),
  });
  /** leave the tutorial behind and start an ordinary night on a fresh seed */
  const freshRun = (): void => {
    const url = new URL(location.href);
    url.searchParams.delete('seed');
    url.searchParams.delete('lehrnacht');
    location.href = url.toString();
  };
  const ui = new Ui({
    root: uiRoot,
    input,
    onRestart: freshRun,
    floors: dir.floors,
  });

  // dropping a token from Der Beutel onto the diorama is the diegetic Setzen
  stage.addEventListener('dragover', (e) => e.preventDefault());
  stage.addEventListener('drop', (e) => {
    e.preventDefault();
    const i = Number((e as DragEvent).dataTransfer?.getData('text/plain'));
    if (Number.isFinite(i)) input.aim.beutel = i;
    input.setzen();
  });

  const vitrine = new Vitrine({
    root: uiRoot,
    progress,
    onExport: (b) => { void downloadReceipt(b.lines, { won: false, seed: b.seed }); },
    // the framed receipt on the wall of your own trophy cabinet is the replay button
    onLehrnacht: () => {
      const url = new URL(location.href);
      url.searchParams.delete('seed');
      url.searchParams.set('lehrnacht', '1');
      location.href = url.toString();
    },
  });

  /**
   * DIE LEHRNACHT, and what comes after it.
   *
   * The director exists only on the tutorial run. The first-encounter asides exist only
   * when it is *not* running — a player being taught the pusher does not also need to be
   * told what a crooked "?" means, and the two would talk over each other on the one
   * night both could fire.
   */
  let lehr: Lehrnacht | null = null;
  const erstmals = lehrnacht ? null : new ErstmalsWatcher({
    slips,
    seen: progress.erstmals,
    onSeen: (id) => {
      if (progress.erstmals.includes(id)) return;
      progress = { ...progress, erstmals: [...progress.erstmals, id] };
      saveProgress(progress);
    },
  });

  /** Remember the night — framed, and never offered again. */
  const frameLehrgeld = (lines: string[], skipped: boolean, seconds: number): void => {
    progress = {
      ...progress,
      lehrgeld: { seed, skipped, seconds: Math.round(seconds), lines: [...lines] },
    };
    saveProgress(progress);
    vitrine.update(progress);
  };


  /**
   * The run is over: bank what happened, open whatever it unlocked, shelve the trophy or
   * the red receipt. `recordRun` is pure and `saveProgress` is wrapped, so a hostile or
   * full localStorage cannot take the game down at the exact moment a player finished it.
   */
  let banked = false;
  const bankRun = (s: Snapshot): void => {
    if (banked || !s.run.over) return;
    banked = true;
    const c = dir.chronicleSummary();
    const summary: RunSummary = {
      seed, won: s.run.won, floor: s.run.floor,
      coins: s.run.muenzen, tickets: s.run.tickets,
      verdachtPeak: c.verdachtPeak, bankBreaks: c.bankBreaks,
      bossesBeaten: c.bossesBeaten, shoves: c.shoves,
      bestComboTags: c.bestComboTags, prizes: c.grabbed,
      bestPayout: c.bestPayout, bestComboValue: c.bestComboValue,
      cleanFloors: c.cleanFloors, maxTagsOnToken: c.maxTagsOnToken,
      zinkenStolen: c.zinkenStolen, confiscated: c.confiscated,
      receipt: dir.receipt(), dayKey: daily ? dailyKey() : undefined,
    };
    const before = progress;
    progress = recordRun(before, summary);
    saveProgress(progress);
    vitrine.update(progress);
    for (const u of newlyUnlocked(before, progress)) ui.say(`NEU IM SAAL: ${u.name} — ${u.note}`);
  };

  const loop = new GameLoop(dir, (s: Snapshot, dt: number) => {
    input.observe(s);
    kit.pushSnapshot(s);
    kit.step(dt);
    ui.render(s);
    hilfe.observe(s);
    audio.push(s.events, { phase: s.run.phase, machine: s.run.activeMachine });
    audio.update(dt, {
      phase: s.run.phase,
      machine: s.run.activeMachine,
      tilt01: s.cabinet?.tilt01 ?? 0,
      topf01: s.cabinet?.topf01 ?? 0,
    });
    // The director runs AFTER the UI has drawn the frame it is talking about, and the
    // asides run only when it is not. Neither can touch the sim; both only read.
    lehr?.observe(s);
    erstmals?.observe(s);
    bankRun(s);
  });

  if (lehrnacht) {
    lehr = new Lehrnacht({
      dir,
      input,
      loop,
      slips,
      root: uiRoot,
      boardEl: () => uiRoot.querySelector('.sh-board'),
      onSkip: () => {
        frameLehrgeld([], true, 0);
        freshRun();
      },
      onFinish: (lines) => frameLehrgeld(lines, false, lehr?.seconds ?? 0),
    });
    // the gate: an input refused here never reaches the log and never reaches the sim
    input.gate = (verb, machine, payload) => lehr!.gate(verb, machine, payload);
  }

  // V opens Die Vitrine; Strg+B exports the run's receipt as a picture.
  const metaKeys = (e: KeyboardEvent): void => {
    if (e.key.toLowerCase() === 'v' && !e.ctrlKey && !e.metaKey) {
      vitrine.toggle();
    } else if (e.key.toLowerCase() === 'b' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      const lines = dir.receipt();
      if (lines.length > 0) {
        void downloadReceipt(lines, { won: dir.sim.won, seed, dayKey: daily ? dailyKey() : undefined });
      }
    }
  };
  window.addEventListener('keydown', metaKeys);

  /**
   * THE FRONT DOOR.
   *
   * What used to be here was a wall of keys — every verb and its letter, in one toast, at
   * boot, to every player forever. That line was this game's entire onboarding and it was
   * doing the two things core-loop.md's opening gate forbids: it explained, and it
   * explained *before* the player had seen anything worth explaining.
   *
   * It is gone. A first-time player is taught by Die Lehrnacht, which says nothing at all
   * until there is a machine in front of them. A returning player gets one line and the
   * reference card is on H, where they will find it because the Lehrnacht told them once.
   */
  if (lehrnacht) {
    ui.say('SPIELHÖLLE');
  } else {
    ui.say('SPIELHÖLLE. Sie schulden dem Haus Geld.');
    ui.say('H halten = Hilfekarte · V = Vitrine');
    if (daily) ui.say(`TAGESLOS ${dailyKey()} — für alle dasselbe Spiel, nur lokal gezählt`);
  }

  loop.start();

  const game: Game = {
    dir, kit, ui, loop, input,
    record: () => ({ seed, inputs: [...dir.inputs], contract: CONTRACT_VERSION }),
    destroy: () => {
      loop.stop();
      input.dispose();
      window.removeEventListener('keydown', metaKeys);
      lehr?.dispose();
      slips.dispose();
      hilfe.dispose();
      audio.dispose();
      dir.sim.free();
    },
  };

  // The E2E harness drives the game by hand so screenshots are reproducible on a box with
  // no GPU: real frames take seconds here, so the tests step the sim rather than wait.
  (window as unknown as { SPIELHOELLE: unknown }).SPIELHOELLE = {
    game,
    seed,
    contract: CONTRACT_VERSION,
    /** step N sim ticks and render one frame, with no wall clock involved */
    advance: (ticks: number) => loop.advance(ticks),
    pause: () => loop.stop(),
    resume: () => loop.start(),
    snapshot: () => dir.snapshot(),
    hash: () => dir.sim.hashWorld(),
    push: (verb: string, machine: MachineId, payload?: unknown) =>
      dir.push(verb as never, machine, payload),
    receipt: () => dir.receipt(),
    stats: () => ({ ...loop.stats, ...kit.stats() }),
    /**
     * DIE LEHRNACHT, for the E2E.
     *
     * `lehrnacht()` is the director's own state — which beat, the clock in game seconds,
     * every line he has printed. `slips` is the printed record independent of the
     * director, so a gate can assert what the player was actually told rather than what
     * the director believes it said.
     *
     * Note that the harness's own `push` deliberately **bypasses the gate**: it is the
     * hand of a test driving the sim, not a player's hand, and a test that had to satisfy
     * the tutorial's beat order to poke the game would be a test of the tutorial. The
     * Lehrnacht E2E drives the player's path through `pressKey`/`click` like a human.
     */
    lehrnacht: () => (lehr ? lehr.state() : null),
    slips: () => [...slips.log],
    erstmals: () => (erstmals ? erstmals.told : []),
    isLehrnacht: lehrnacht,
  };

  return game;
}

// ---------------------------------------------------------------- entry
const root = document.getElementById('app');
if (root) {
  boot(root).catch((err) => {
    // a boot failure must be LOUD: a black canvas that passes a smoke test is the known
    // trap on this box, so failure gets written where a screenshot will show it.
    const pre = document.createElement('pre');
    pre.className = 'sh-boot-error';
    pre.textContent = `SPIELHÖLLE konnte nicht starten:\n${String(err && (err as Error).stack || err)}`;
    root.appendChild(pre);
    console.error(err);
  });
}
